/**
 * Readers for the judge's stored verdict. The comparison is schemaless in the store, so
 * every read refuses a wrong type instead of trusting it; a run whose model skipped a
 * field just shows less.
 */

export const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export const rec = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

/** A string worth rendering; blank and non-strings read as absent. */
export const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

/** The non-blank strings of a list; anything else reads as an empty list. */
export const strings = (v: unknown): string[] =>
  (Array.isArray(v) ? v : []).filter((item): item is string => typeof item === 'string' && item.trim() !== '');

/** The records of a list; anything else reads as an empty list. */
export const records = (v: unknown): Record<string, unknown>[] =>
  (Array.isArray(v) ? v : []).map(rec).filter((item): item is Record<string, unknown> => item !== null);

/** One of a closed vocabulary, or null for anything outside it. */
export const oneOf = <T extends string>(v: unknown, options: readonly T[]): T | null =>
  typeof v === 'string' && (options as readonly string[]).includes(v) ? (v as T) : null;

/** The judge's bucket off a stored comparison. */
export const comparisonBucket = (comparison: Record<string, unknown> | null): string | null =>
  str(comparison?.['comparisonBucket']);

/** What the agent's reply covered that the AI's did not, as the judge listed it. */
export const missingPhrases = (comparison: Record<string, unknown> | null): string[] =>
  strings(rec(comparison?.['analysis'])?.['missingKeywords']);

/** The investigator's verdict off a run's stored envelope; null when the run was never investigated. */
export const investigationVerdict = (investigation: unknown): Record<string, unknown> | null =>
  rec(rec(investigation)?.['verdict']);

/** The stored envelope's lifecycle state; null when the run was never investigated. */
export const investigationStatus = (investigation: unknown): 'pending' | 'completed' | 'failed' | null => {
  const status = rec(investigation)?.['status'];
  return status === 'pending' || status === 'completed' || status === 'failed' ? status : null;
};

export interface ScorePart {
  readonly key: string;
  readonly label: string;
  readonly max: number;
}

/** The criteria the judge scores each dimension on, and the points each carries. */
export const SCORE_PARTS = {
  completeness: [
    { key: 'issueCoverage', label: 'Issue coverage', max: 30 },
    { key: 'resolutionClarity', label: 'Resolution clarity', max: 30 },
    { key: 'nextStepsAndOwnership', label: 'Next steps & ownership', max: 25 },
    { key: 'policyConstraintDisclosure', label: 'Policy disclosure', max: 15 },
  ],
  personalization: [
    { key: 'customerIdentity', label: 'Customer identity', max: 10 },
    { key: 'intentUnderstanding', label: 'Intent understanding', max: 25 },
    { key: 'orderTransactionAwareness', label: 'Order awareness', max: 25 },
    { key: 'issueSpecificLanguage', label: 'Issue-specific language', max: 20 },
    { key: 'productLevelPersonalization', label: 'Product personalization', max: 10 },
    { key: 'actionPersonalization', label: 'Action personalization', max: 10 },
  ],
} as const satisfies Record<string, readonly ScorePart[]>;
